// Undo/Redo מקומי בסשן על מצבי מסמך. פעולות רציפות (גרירה, הקלדה) מתאחדות לצעד אחד לפי מפתח.
import { useCallback, useRef, useState } from 'react';
import type { BannerDoc } from '@bb/schema';

interface H { past: BannerDoc[]; present: BannerDoc; future: BannerDoc[] }
const LIMIT = 200;

export function useDocHistory(initial: BannerDoc) {
  const [h, setH] = useState<H>({ past: [], present: initial, future: [] });
  const last = useRef<{ key: string | null; at: number }>({ key: null, at: 0 });

  /** שינוי מסמך. coalesceKey: שינויים רצופים עם אותו מפתח (בתוך 1.2 שניות) מתאחדים */
  const apply = useCallback((next: BannerDoc, coalesceKey?: string) => {
    const now = Date.now();
    const merge = !!coalesceKey && last.current.key === coalesceKey && now - last.current.at < 1200;
    last.current = { key: coalesceKey ?? null, at: now };
    setH((s) => {
      if (next === s.present) return s;
      if (merge) return { ...s, present: next, future: [] };
      return { past: [...s.past, s.present].slice(-LIMIT), present: next, future: [] };
    });
  }, []);

  const undo = useCallback(() => {
    last.current = { key: null, at: 0 };
    setH((s) => (s.past.length ? { past: s.past.slice(0, -1), present: s.past[s.past.length - 1], future: [s.present, ...s.future] } : s));
  }, []);
  const redo = useCallback(() => {
    last.current = { key: null, at: 0 };
    setH((s) => (s.future.length ? { past: [...s.past, s.present], present: s.future[0], future: s.future.slice(1) } : s));
  }, []);
  /** איפוס מלא (טעינה מהשרת) — מוחק היסטוריה */
  const reset = useCallback((doc: BannerDoc) => { last.current = { key: null, at: 0 }; setH({ past: [], present: doc, future: [] }); }, []);
  /** "עצירת" איחוד — הפעולה הבאה תהיה צעד חדש */
  const breakMerge = useCallback(() => { last.current = { key: null, at: 0 }; }, []);

  return { doc: h.present, apply, undo, redo, reset, breakMerge, canUndo: h.past.length > 0, canRedo: h.future.length > 0 };
}
